// English comments
import { emit } from "../core/events";

const OP_PING = 2;

function randNonce() {
  // simple nonce generator
  return crypto.getRandomValues(new Uint32Array(4)).join("-");
}

type PingResult = { ok: boolean; mac?: string; code?: string };

export function startWasmPing(
  // English: lazily get the wasm instance
  getWasm: () => Promise<any>,
  // English: ping options
  opts: { intervalMs?: number; maxMisses?: number; debug?: boolean } = {},
  // English: license token is passed explicitly (no window globals)
  licenseToken: string,
) {
  const interval = opts.intervalMs ?? 1500;
  const maxMisses = opts.maxMisses ?? 2;
  let misses = 0;
  let timer: number | undefined;

  async function tick() {
    // Skip while hidden to avoid background noise
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    try {
      const wasm = await getWasm();
      const nonce = randNonce();
      const t = Date.now();

      const raw = wasm.dispatch(
        OP_PING,
        JSON.stringify({
          license_token: licenseToken, // <-- no window usage
          host: location.hostname,
          nonce,
          t,
        }),
      );
      const res: PingResult = JSON.parse(raw);

      if (res?.ok && res.mac) {
        // treat as alive (server-side verification can be added later)
        misses = 0;
        if (opts.debug) console.debug("[JSentinel][ping] mac:", res.mac);
      } else {
        misses++;
        if (opts.debug) console.debug("[JSentinel][ping] fail:", res?.code);
      }

      if (misses > maxMisses) {
        emit({ type: "tamper.jsentinel", data: { reason: "PING_MISSES", ts: Date.now() } });
        misses = 0;
      }
    } catch (e) {
      misses++;
      if (misses > maxMisses) {
        emit({ type: "tamper.jsentinel", data: { reason: "EXCEPTION", ts: Date.now() } });
        misses = 0;
      }
      if (opts.debug) console.debug("[JSentinel][ping] exception", e);
    }
  }

  timer = setInterval(tick, interval) as unknown as number;
  void tick(); // first immediate run

  return () => {
    if (timer !== undefined) clearInterval(timer);
  };
}
